import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import CategoryTile from "../components/CategoryTile";
import EventCarousel from "../components/EventCarousel";
import Hero from "../components/Hero";
import Loading from "../components/Loading";
import PerformerChip from "../components/PerformerChip";
import SpotlightCard from "../components/SpotlightCard";
import type { Recommendations } from "../types/api";
import styles from "./HomePage.module.css";

export default function HomePage() {
  const [data, setData] = useState<Recommendations | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .recommendations()
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []);

  // Pick spotlight event before render
  const spotlight = useMemo(() => {
    if (!data) return null;
    // Pick the event with the soonest date that has > $100 minPrice for "drama"
    const candidates = [...data.highInventoryEvents].sort(
      (a, b) =>
        new Date(a.datetime).getTime() - new Date(b.datetime).getTime(),
    );
    const dramatic =
      candidates.find((e) => e.minPrice >= 100) ?? candidates[0];
    return dramatic ?? null;
  }, [data]);

  const venues = data?.venues ?? [];
  const eventsByCategory = data?.eventsByCategory ?? {};

  const venuesById = useMemo(
    () => Object.fromEntries(venues.map((v) => [v.id, v])),
    [venues],
  );

  const categoryEventCounts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(eventsByCategory).map(([k, v]) => [k, v.length]),
      ),
    [eventsByCategory],
  );

  if (error) {
    return (
      <div className="container">
        <div className="empty">Failed to load: {error}</div>
      </div>
    );
  }
  if (!data || !spotlight) {
    return (
      <>
        <Hero />
        <div className="container">
          <Loading />
        </div>
      </>
    );
  }

  const spotlightVenue = venuesById[spotlight.venueId];

  return (
    <>
      <Hero />

      <div className="container">
        {/* Browse by category - visual tile grid */}
        <h2 className="section-title">
          Browse by category
          <Link to="/search" className="section-title-link">
            See all events →
          </Link>
        </h2>
        <div className={styles.categoryGrid}>
          {data.categories.map((c) => (
            <CategoryTile
              key={c.id}
              id={c.id}
              name={c.name}
              count={categoryEventCounts[c.id]}
            />
          ))}
        </div>

        {/* Spotlight - one big featured event */}
        <h2 className="section-title">In the spotlight</h2>
        <SpotlightCard event={spotlight} venue={spotlightVenue} />

        {/* Trending */}
        <h2 className="section-title">
          Trending now
          <Link to="/search" className="section-title-link">
            See more →
          </Link>
        </h2>
        <EventCarousel events={data.highInventoryEvents} venues={venues} />

        {/* Featured performers */}
        <h2 className="section-title">Featured performers</h2>
        <div className={styles.performerGrid}>
          {data.featuredPerformers.map((p) => (
            <PerformerChip key={p.id} performer={p} />
          ))}
        </div>

        {/* Per-category rows */}
        {data.categories.map((cat) => {
          const events = eventsByCategory[cat.id];
          if (!events || events.length === 0) return null;
          return (
            <div key={cat.id}>
              <h2 className="section-title">
                {cat.name}
                <Link
                  to={`/search?category=${cat.id}`}
                  className="section-title-link"
                >
                  See all {cat.name.toLowerCase()} →
                </Link>
              </h2>
              <EventCarousel events={events} venues={venues} />
            </div>
          );
        })}

        {/* Footer reassurance band */}
        <section className={styles.reassurance}>
          <div className={styles.reassuranceCard}>
            <span className={styles.reassuranceIcon}>✓</span>
            <div>
              <div className={styles.reassuranceTitle}>100% guaranteed</div>
              <div className={styles.reassuranceDesc}>
                Every order backed by our buyer guarantee.
              </div>
            </div>
          </div>
          <div className={styles.reassuranceCard}>
            <span className={styles.reassuranceIcon}>⚡</span>
            <div>
              <div className={styles.reassuranceTitle}>Instant confirmation</div>
              <div className={styles.reassuranceDesc}>
                Tickets delivered to your inbox in seconds.
              </div>
            </div>
          </div>
          <div className={styles.reassuranceCard}>
            <span className={styles.reassuranceIcon}>$</span>
            <div>
              <div className={styles.reassuranceTitle}>All-in pricing</div>
              <div className={styles.reassuranceDesc}>
                The price you see is the price you pay.
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
